import React from "react";
import { CloseOutlined } from "@ant-design/icons";
import { Button, Checkbox, Drawer, InputNumber, message, Select, Typography } from "antd";
import { IDocumentType } from "store/lobby/lobby-model";
import { isCpfDocumentType, isDriverLicenseDocumentType, isRequiredDocumentType, resolveDocumentType } from "utils/documentType";
import { validationNumber } from "utils/form/validations";
import { validationCharacters } from "utils/functions";
import useTranslation from "utils/hooks/useTranslation";
import * as S from "../styled/index.styled";
import { SwitchComponent } from "./configurationTabPane/components/SwitchComponent";

const { Title } = Typography;
const { Option } = Select;

const Footer = (props) => {
  const CANCEL = useTranslation({ id: "cancel" });
  const CONFIRM = useTranslation({ id: "confirm" });

  return (
    <S.DrawerFooter>
      <div className="left">
        <Button className="btnCancel" onClick={props.onClose}>
          {CANCEL}
        </Button>
      </div>
      <div className="right row">
        <div className="col-sm">
          <Button type="primary" className="btn" onClick={props.onConfirm}>
            {CONFIRM}
          </Button>
        </div>
      </div>
    </S.DrawerFooter>
  );
};

const DrawerConfiguration = ({
  visibleDrawer,
  locationsConfigurationsUpdateConfigurationActive,
  locationsConfigurationsHideDrawer,
  configurationActive,
  locationsConfigurationsUpdateFormFieldValue,
}) => {
  const DESCRIPTION_NAME = configurationActive?.descriptionName;
  const DESCRIPTION = configurationActive?.description;
  const TIME_IN_HOURS = useTranslation({ id: "time_in_hours" });
  const ENTER_THE_NUMBER_OF_HOURS = useTranslation({ id: "enter_the_number_of_hours" });
  const ENABLE_OR_DISABLE_PROCESS = useTranslation({ id: "enable_or_disable_process" });
  const EDIT_CONFIG = useTranslation({ id: "edit_config" });
  const EDITING_UNIT_SETTINGS = useTranslation({ id: "editing_unit_settings" });
  const PROCESS = useTranslation({ id: "processes" });
  const IDENTIFICATION_DOCUMENT = useTranslation({ id: "identification_document" });
  const DRIVER_LICENSE = useTranslation({ id: "driver_license" });
  const REQUIRE_DRIVER_DOCUMENT = useTranslation({ id: "require_driver_document" });
  const INTERN_DROPHOOK_TYPE = useTranslation({ id: "internal" });
  const EXTERN_DROPHOOK_TYPE = useTranslation({ id: "external" });
  const INTERN_EXTERN_DROPHOOK_TYPE = useTranslation({ id: "internal_external" });
  const DROP_HOOK_OPTIONS_REQUIRED = useTranslation({ id: "drop_hook_options_required" });
  const BRAZIL_MERCOSUL = useTranslation({ id: "brazil_mercosul" });
  const CUSTOM = useTranslation({ id: "custom_default" });
  const MEXICO = useTranslation({ id: "mexico" });
  const SELECT_AN_OPTION = useTranslation({ id: "select_an_option" });
  const REQUIRED_FIELD = useTranslation({ id: "required_field" });
  const SELECT_FORMAT_PLATE = useTranslation({ id: "select_format_plate" });

  const plateFormatOptions = [
    { id: 0, description: `ABC1D23 - ${BRAZIL_MERCOSUL}` },
    { id: 2, description: `ABC-123-A - ${MEXICO}` },
    { id: 1, description: `ABC1234567 - ${CUSTOM}` },
  ];
  const onConfirm = () => {
    const dropHookActive = "dropHookActive";
    const dropHookTypeActive = "dropHookTypeActive";
    const plateFormatActive = "plateFormatType";
    if (
      (configurationActive?.dropHookType === null || configurationActive?.dropHookType === 0) &&
      configurationActive.configuration &&
      configurationActive.configurationName === dropHookActive
    ) {
      message.error(DROP_HOOK_OPTIONS_REQUIRED);
      return;
    }

    locationsConfigurationsHideDrawer();

    const {
      isCheckinTime,
      expirationTime,
      documentType,
      dropHookType,
      plateFormatType,
      configurationName,
      configuration,
      hasDocumentIdentification,
      hasDropHookOptions,
      hasPlateFormat,
    } = configurationActive || {};

    let value;

    if (isCheckinTime) {
      value = expirationTime ?? 0;
    } else if (hasDocumentIdentification && [0, 1, 2, 3].includes(documentType)) {
      value = documentType;
    } else if (hasDropHookOptions && [1, 2, 3].includes(dropHookType)) {
      locationsConfigurationsUpdateFormFieldValue(dropHookTypeActive, dropHookType);
      value = configuration;
    } else if (hasPlateFormat && [0, 1, 2].includes(plateFormatType)) {
      locationsConfigurationsUpdateFormFieldValue(plateFormatActive, plateFormatType);
      value = plateFormatType;
    } else if (hasDropHookOptions && dropHookType === null && configurationName === dropHookActive) {
      locationsConfigurationsUpdateFormFieldValue(dropHookTypeActive, null);
    } else {
      value = configuration;
    }

    locationsConfigurationsUpdateFormFieldValue(configurationName, value);
  };

  const onClose = () => {
    locationsConfigurationsHideDrawer();
  };

  const handleToggleChange = (checked: boolean) => {
    locationsConfigurationsUpdateConfigurationActive({
      ...configurationActive,
      configuration: checked,
      dropHookType: !checked && configurationActive.dropHookType ? null : configurationActive.dropHookType,
    });
  };

  const handleCheckinExpirationTime = (value) => {
    locationsConfigurationsUpdateConfigurationActive({
      ...configurationActive,
      expirationTime: value,
    });
  };

  const validation = (e) => {
    validationCharacters(e, validationNumber);
  };

  const onChangeDriverLicense = (type: IDocumentType.IdentificationDocument | IDocumentType.DriverLicense) => {
    const documentType = resolveDocumentType(type === IDocumentType.DriverLicense, isRequiredDocumentType(configurationActive.documentType));

    locationsConfigurationsUpdateConfigurationActive({
      ...configurationActive,
      documentType,
    });
  };

  const onChangeRequiredDocument = (checked: boolean) => {
    const documentType = resolveDocumentType(isDriverLicenseDocumentType(configurationActive.documentType), checked);

    locationsConfigurationsUpdateConfigurationActive({
      ...configurationActive,
      documentType,
    });
  };

  const onChangeDropHookType = (type) => {
    locationsConfigurationsUpdateConfigurationActive({
      ...configurationActive,
      dropHookType: type,
    });
  };

  const handlePlateFormatSelectionChange = (type) => {
    locationsConfigurationsUpdateConfigurationActive({
      ...configurationActive,
      plateFormatType: type,
    });
  };

  return (
    <Drawer
      title={EDIT_CONFIG}
      placement="right"
      open={visibleDrawer}
      width="460"
      closeIcon={false}
      maskClosable={true}
      closable={true}
      onClose={() => {
        locationsConfigurationsUpdateConfigurationActive(null);
        locationsConfigurationsHideDrawer();
      }}
      destroyOnClose={false}
      extra={
        <S.CloseDrawer onClick={() => locationsConfigurationsHideDrawer()} data-testid="closeIcon">
          <CloseOutlined className="closeIcon" />
        </S.CloseDrawer>
      }
      footer={<Footer onClose={onClose} onConfirm={onConfirm} />}
    >
      <S.TitleDescription>{EDITING_UNIT_SETTINGS}</S.TitleDescription>
      <S.ProcessDescription>{PROCESS}</S.ProcessDescription>
      <S.ProcessTitle>{configurationActive ? DESCRIPTION_NAME : ""}</S.ProcessTitle>
      {configurationActive?.isCheckinTime ? (
        <>
          <Title level={5} style={S.ConfigurationDescription}>
            {DESCRIPTION}
          </Title>
          <S.Description>{TIME_IN_HOURS}</S.Description>
          <InputNumber
            style={{ width: "100%" }}
            onKeyPress={(e) => validation(e)}
            data-testid="checkinExpirationTime"
            placeholder={ENTER_THE_NUMBER_OF_HOURS}
            min={0}
            max={999}
            defaultValue={configurationActive.expirationTime}
            onChange={handleCheckinExpirationTime}
          />
        </>
      ) : (
        <>
          <S.Description>{ENABLE_OR_DISABLE_PROCESS}</S.Description>
          <SwitchComponent
            title={configurationActive?.description}
            checked={configurationActive?.configuration}
            name={configurationActive?.descriptionName}
            onChange={handleToggleChange}
            dataTestId={`toggle-${configurationActive?.configurationName}`}
            disabled={configurationActive?.disabled}
          />
        </>
      )}
      {configurationActive?.hasDocumentIdentification && (
        <S.DocumentsContainer>
          <Checkbox
            data-testid="identificationNumber"
            checked={isCpfDocumentType(configurationActive.documentType)}
            onChange={() => onChangeDriverLicense(IDocumentType.IdentificationDocument)}
          >
            {IDENTIFICATION_DOCUMENT}
          </Checkbox>
          <Checkbox
            data-testid="driverLicenseNumber"
            checked={isDriverLicenseDocumentType(configurationActive.documentType)}
            onChange={() => onChangeDriverLicense(IDocumentType.DriverLicense)}
          >
            {DRIVER_LICENSE}
          </Checkbox>
          <S.RequiredDocumentContainer>
            <Checkbox
              data-testid="requiredDriverDocument"
              checked={isRequiredDocumentType(configurationActive.documentType)}
              onChange={(event) => onChangeRequiredDocument(event.target.checked)}
            >
              {REQUIRE_DRIVER_DOCUMENT}
            </Checkbox>
          </S.RequiredDocumentContainer>
        </S.DocumentsContainer>
      )}
      {configurationActive?.hasDropHookOptions && (
        <S.DocumentsContainer>
          <Checkbox
            data-testid="internDropHook"
            disabled={!configurationActive?.configuration}
            checked={configurationActive.dropHookType === 1}
            onChange={() => onChangeDropHookType(1)}
          >
            {INTERN_DROPHOOK_TYPE}
          </Checkbox>
          <Checkbox
            data-testid="externDropHook"
            disabled={!configurationActive?.configuration}
            checked={configurationActive.dropHookType === 2}
            onChange={() => onChangeDropHookType(2)}
          >
            {EXTERN_DROPHOOK_TYPE}
          </Checkbox>
          <Checkbox
            data-testid="internAndExternDropHook"
            disabled={!configurationActive?.configuration}
            checked={configurationActive.dropHookType === 3}
            onChange={() => onChangeDropHookType(3)}
          >
            {INTERN_EXTERN_DROPHOOK_TYPE}
          </Checkbox>
        </S.DocumentsContainer>
      )}
      {configurationActive.hasPlateFormat && plateFormatOptions.some((plateFormatId) => plateFormatId.id === configurationActive?.plateFormatType) && (
        <S.FormItemFieldName
          name="plateFormatType"
          label={`${SELECT_FORMAT_PLATE}*`}
          rules={[
            {
              required: true,
              message: REQUIRED_FIELD,
            },
          ]}
        >
          <Select
            data-testid="plate-format-select"
            placeholder={SELECT_AN_OPTION}
            onChange={handlePlateFormatSelectionChange}
            style={{ width: "100%" }}
            disabled={!plateFormatOptions.length}
            defaultValue={configurationActive.plateFormatType}
          >
            {plateFormatOptions.map((plateFormat) => (
              <Option key={plateFormat.id} value={plateFormat.id}>
                {plateFormat.description}
              </Option>
            ))}
          </Select>
        </S.FormItemFieldName>
      )}
    </Drawer>
  );
};

export default DrawerConfiguration;
